import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, notification, Form, Col, Button, message } from 'antd';
import axios from 'axios';
import store from 'store';
import { useQueryClient } from '@tanstack/react-query';
import { baseURL } from '../../utils/helpers';

import ImagesUploader from '../../components/ImagesUploader';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { useBanners } from '../../hooks/useMenuQuery';

const authToken = store.get('authenticationToken');

function Banners() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent } = useContext(LayoutContext);
  const { doFetch: updateBannerImagesFetch } = useFetch();
  const { data: banners = [], isLoading: isLoadingBanners, error: bannersError } = useBanners();

  const [uploadFileList, setUploadFileList] = useState([]); // State in the parent to manage the file list
  const [deleteFileList, setDeleteFileList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialImages = banners.map(banner => ({
    uid: banner.id, // Use the ID as uid to keep it unique
    name: `Banner ${banner.id}`, // Better name for banners
    status: 'done', // Mark these as already uploaded
    url: banner.image_url || `${baseURL}/files/banner-images/${banner.id}`, // The link to the image
  }));

  // Handle changes in the file list, passed to the child component
  const handleUploadFileListChange = newFileList => {
    setUploadFileList(newFileList); // Update the file list in the parent
  };

  useEffect(() => {
    setHeaderComponent(<b>{`Banners`}</b>);

    return () => {
      setHeaderComponent(null);
    };
  }, []);

  const handleOnSuccess = () => {
    // Invalidate the banners query to refetch updated data
    queryClient.invalidateQueries({ queryKey: ['banners'] });

    notification.success({
      placement: 'bottomRight',
      message: 'Banners updated successfully.',
      duration: 3,
    });
    navigate('/');
    return;
  };

  const handleOnError = (errorMessage) => {
    notification.error({
      placement: 'bottomRight',
      message: 'Error updating banners',
      description: errorMessage,
      duration: 5,
    });
  };

  const updateBannerImages = async formValues => {
    // Validate that we have something to submit
    if (uploadFileList.length === 0 && deleteFileList.length === 0) {
      message.warning('No changes detected. Please add or remove images before saving.');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      // Add new images (files) to formData
      uploadFileList.forEach(file => {
        if (!file.url) {
          // Only add newly uploaded images, not existing ones
          formData.append('upload_images', file.originFileObj); // Ensure you're sending the file object
        }
      });

      // Add deleted image IDs to formData
      deleteFileList.forEach(file => {
        formData.append('delete_images', file.uid); // Send the ID of the deleted images
      });

      const response = await axios.post(`${baseURL}/${URLS.manageBannerImages()}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `${authToken}`,
        },
      });

      handleOnSuccess();
    } catch (error) {
      console.error('Error uploading images:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      handleOnError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (isLoadingBanners) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading banners...</div>
      </div>
    );
  }

  // Show error state
  if (bannersError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        <div>Error loading banners: {bannersError.message}</div>
      </div>
    );
  }

  return (
    <Form onFinish={updateBannerImages}>
      <Row>
        <Col span={12}>
          <Form.Item label="Banner Images:" className="product-image-upload">
            <ImagesUploader
              fileList={uploadFileList} // Pass the uploadFileList to the child component
              setFileList={handleUploadFileListChange} // Pass the callback to update uploadFileList
              initialImages={initialImages}
              deletedImages={deleteFileList}
              setDeletedImages={setDeleteFileList}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row className="m-t-30 f-r">
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Form.Item>
      </Row>
    </Form>
  );
}

export default Banners;
